import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect } from "react";
import { PILLARS, getPillar, itemsForPillar, groupByKind, setActivePillar, type PillarId } from "@/lib/method";
import { KIND_LABEL, KIND_NOUN, type EcosystemItem } from "@/lib/ecosystem";
import { track } from "@/lib/analytics";

const PILLAR_IDS = new Set(PILLARS.map((p) => p.id));

export const Route = createFileRoute("/method/$pillar")({
  loader: ({ params }) => {
    if (!PILLAR_IDS.has(params.pillar as PillarId)) throw notFound();
    return { pillarId: params.pillar as PillarId };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Pillar not found — KAfrequency Method" }, { name: "robots", content: "noindex" }] };
    }
    const p = getPillar(loaderData.pillarId)!;
    const url = `https://buddhaka.com/method/${p.id}`;
    return {
      meta: [
        { title: `${p.name} — The KAfrequency Method` },
        { name: "description", content: `${p.essence} ${p.invitation}` },
        { property: "og:title", content: `${p.name} — The KAfrequency Method` },
        { property: "og:description", content: p.invitation },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },

  component: PillarPage,
  notFoundComponent: PillarNotFound,
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <p className="text-sm text-muted-foreground">{error.message}</p>
      <Link to="/method" className="mt-6 inline-block text-gold hover:text-gold-luminous">← Return to the Method</Link>
    </div>
  ),
});

function PillarPage() {
  const { pillarId } = Route.useLoaderData();
  const pillar = getPillar(pillarId)!;
  const items = itemsForPillar(pillarId);
  const grouped = groupByKind(items);
  const index = PILLARS.findIndex((p) => p.id === pillarId);
  const prev = PILLARS[(index - 1 + PILLARS.length) % PILLARS.length];
  const next = PILLARS[(index + 1) % PILLARS.length];

  useEffect(() => {
    track("pillar_view", { pillar: pillarId });
    setActivePillar(pillarId);
  }, [pillarId]);

  const kinds: Array<{ key: keyof typeof grouped; heading: string }> = [
    { key: "activation", heading: "Practice" },
    { key: "music",      heading: "Listen" },
    { key: "wisdom",     heading: "Read" },
    { key: "book",       heading: "Study" },
  ];

  return (
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
      <nav aria-label="Breadcrumb" className="mb-8 text-xs uppercase tracking-[0.3em] text-muted-foreground">
        <Link to="/method" className="hover:text-gold">The Method</Link>
        <span className="mx-2 text-gold/50">·</span>
        <span className="text-gold">{pillar.name}</span>
      </nav>

      <header className="mx-auto max-w-3xl">
        <p className="text-[10px] uppercase tracking-[0.4em] text-gold">Pillar {String(index + 1).padStart(2, "0")} of 10</p>
        <div className="mt-4 flex items-baseline gap-4">
          <span aria-hidden className="font-serif text-5xl text-gold-luminous">{pillar.glyph}</span>
          <h1 className="font-serif text-5xl sm:text-7xl">{pillar.name}</h1>
        </div>
        <p className="mt-6 font-serif text-2xl text-gold-luminous">{pillar.essence}</p>
        <p className="mt-4 text-base text-muted-foreground sm:text-lg">{pillar.invitation}</p>
      </header>

      <div className="mt-16 space-y-16">
        {kinds.map(({ key, heading }) => {
          const list = grouped[key];
          if (!list.length) return null;
          return (
            <section key={key} aria-labelledby={`pillar-${key}`}>
              <div className="flex items-baseline justify-between border-b hairline pb-3">
                <h2 id={`pillar-${key}`} className="font-serif text-2xl">
                  {heading} <span className="text-muted-foreground text-sm">· {list.length} {list.length === 1 ? KIND_NOUN[key as keyof typeof KIND_NOUN] : KIND_NOUN[key as keyof typeof KIND_NOUN] + "s"}</span>
                </h2>
                <span className="text-[10px] uppercase tracking-[0.3em] text-gold">{KIND_LABEL[key as keyof typeof KIND_LABEL]}</span>
              </div>
              <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {list.slice(0, 9).map((item) => (
                  <li key={`${item.kind}:${item.slug}`}>
                    <PillarItem item={item} onPress={() => track("pillar_engage", { pillar: pillarId, kind: item.kind, slug: item.slug })} />
                  </li>
                ))}
              </ul>
            </section>
          );
        })}

        {items.length === 0 && (
          <p className="rounded-2xl border hairline bg-obsidian/40 p-8 text-center text-sm text-muted-foreground">
            This pillar is a horizon. New transmissions are on the way.
          </p>
        )}
      </div>

      <nav aria-label="Adjacent pillars" className="mt-24 grid gap-4 sm:grid-cols-2">
        <Link
          to="/method/$pillar"
          params={{ pillar: prev.id }}
          className="group rounded-2xl border hairline p-6 hover:border-gold"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">← Previous pillar</p>
          <p className="mt-2 font-serif text-xl text-ivory group-hover:text-gold-luminous">{prev.name}</p>
        </Link>
        <Link
          to="/method/$pillar"
          params={{ pillar: next.id }}
          className="group rounded-2xl border hairline p-6 text-right hover:border-gold"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Next pillar →</p>
          <p className="mt-2 font-serif text-xl text-ivory group-hover:text-gold-luminous">{next.name}</p>
        </Link>
      </nav>
    </div>
  );
}

function PillarItem({ item, onPress }: { item: EcosystemItem; onPress: () => void }) {
  if (item.kind === "music") {
    return (
      <Link to="/music/$slug" params={{ slug: item.slug }} onClick={onPress}
        className="group flex h-full flex-col gap-2 rounded-xl border hairline bg-obsidian/60 p-5 backdrop-blur transition-all hover:border-gold hover:bg-obsidian/80">
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Song</p>
        <p className="font-serif text-lg text-ivory group-hover:text-gold-luminous">{item.title}</p>
        <p className="text-xs text-muted-foreground">{item.subtitle}</p>
      </Link>
    );
  }
  if (item.kind === "wisdom") {
    return (
      <Link to="/wisdom/$slug" params={{ slug: item.slug }} onClick={onPress}
        className="group flex h-full flex-col gap-2 rounded-xl border hairline bg-obsidian/60 p-5 backdrop-blur transition-all hover:border-gold hover:bg-obsidian/80">
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Teaching</p>
        <p className="font-serif text-lg text-ivory group-hover:text-gold-luminous">{item.title}</p>
        <p className="text-xs text-muted-foreground">{item.subtitle}</p>
      </Link>
    );
  }
  if (item.kind === "activation") {
    return (
      <Link to="/activations/$slug" params={{ slug: item.slug }} onClick={onPress}
        className="group flex h-full flex-col gap-2 rounded-xl border hairline bg-obsidian/60 p-5 backdrop-blur transition-all hover:border-gold hover:bg-obsidian/80">
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Activation</p>
        <p className="font-serif text-lg text-ivory group-hover:text-gold-luminous">{item.title}</p>
        <p className="text-xs text-muted-foreground">{item.subtitle}</p>
      </Link>
    );
  }
  return (
    <Link to="/books/$slug" params={{ slug: item.slug }} onClick={onPress}
      className="group flex h-full flex-col gap-2 rounded-xl border hairline bg-obsidian/60 p-5 backdrop-blur transition-all hover:border-gold hover:bg-obsidian/80">
      <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Book</p>
      <p className="font-serif text-lg text-ivory group-hover:text-gold-luminous">{item.title}</p>
      <p className="text-xs text-muted-foreground">{item.subtitle}</p>
    </Link>
  );
}

function PillarNotFound() {
  return (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <p className="text-[10px] uppercase tracking-[0.4em] text-gold">Unmapped</p>
      <h1 className="mt-4 font-serif text-4xl">That pillar is not part of the Method.</h1>
      <Link to="/method" className="mt-8 inline-block rounded-full border border-gold px-6 py-3 text-sm text-gold-luminous hover:bg-gold hover:text-obsidian">
        Return to the ten
      </Link>
    </div>
  );
}
